'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cx } from '../../../internal/cx.js';
import { CheckGlyph, ChevronDownGlyph, CloseGlyph, RetryGlyph, SearchGlyph, } from '../../../internal/icons.js';
import { useFieldContext, useFieldControl } from '../form-field/index.js';
import { Spinner } from '../../feedback/spinner/index.js';
const DEFAULT_MESSAGES = {
    loading: 'Loading…',
    error: 'Couldn’t load options.',
    retry: 'Try again',
    create: (q) => `Create “${q}”`,
    remove: (label) => `Remove ${label}`,
    added: (label) => `${label} added`,
    removed: (label) => `${label} removed`,
    more: (shown, total) => `Showing ${shown} of ${total}. Type to narrow the list.`,
    showOptions: 'Show options',
    closeOptions: 'Close options',
};
/** True when `query` appears in the option's label, hint or keywords (case-insensitive). */
export function matchComboboxOption(option, query) {
    const q = query.toLowerCase();
    return (option.label.toLowerCase().includes(q) ||
        (option.hint?.toLowerCase().includes(q) ?? false) ||
        (option.keywords?.toLowerCase().includes(q) ?? false));
}
/** 0: label starts with the query; 1: a word in the label does; 2: anything else. */
function matchRank(option, query) {
    const label = option.label.toLowerCase();
    const q = query.toLowerCase();
    if (label.startsWith(q))
        return 0;
    if (label.includes(` ${q}`) || label.includes(`(${q}`) || label.includes(`-${q}`))
        return 1;
    return 2;
}
function itemDisabled(item) {
    return !item || (item.kind === 'option' && !!item.option.disabled);
}
/**
 * Searchable select. The input is the combobox and the options are a listbox
 * (WAI-ARIA APG combobox pattern with `aria-activedescendant`): single or
 * multiple selection, grouped options with media and hints, async search with
 * debounce and abort, a create option and an inline mode for pickers.
 * Keyboard: Down/Up move, Home/End jump, PageDown/PageUp skip ten, Enter
 * selects, Escape closes, Alt+Down/Alt+Up open and close, typing filters.
 */
export const Combobox = forwardRef(function Combobox({ options = [], value: valueProp, defaultValue = null, onValueChange, multiple = false, values: valuesProp, defaultValues, onValuesChange, valueOptions, inputValue: inputProp, onInputValueChange, filter = matchComboboxOption, sortByMatch = false, onSearch, searchDebounce = 250, minSearchLength = 0, onCreate, clearOption, renderOption, limit, loading: loadingProp = false, emptyMessage = 'No matches', invalid, size = 'md', clearable = true, inline = false, closeOnSelect, open: openProp, defaultOpen = false, onOpenChange, listLabel, messages: messagesProp, placeholder, className, onKeyDown, onBlur, ...rest }, ref) {
    const id = useId();
    const listId = `mn-combobox-${id}-list`;
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const field = useFieldContext();
    // In multiple and inline modes the input is a search box; in single mode it shows the selected label.
    const textIsQuery = multiple || inline;
    const shouldClose = closeOnSelect ?? !(multiple || inline);
    const [openState, setOpenState] = useState(defaultOpen);
    const open = inline ? true : (openProp ?? openState);
    const setOpen = useCallback((next) => {
        if (inline)
            return;
        setOpenState(next);
        onOpenChange?.(next);
    }, [inline, onOpenChange]);
    // ----- Selection -----
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [internalValues, setInternalValues] = useState(defaultValues ?? []);
    const value = valueProp !== undefined ? valueProp : internalValue;
    const values = valuesProp ?? internalValues;
    const selectedValues = useMemo(() => (multiple ? values : value != null ? [value] : []), [multiple, values, value]);
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    // ----- Async search -----
    const [results, setResults] = useState(null);
    const [searchStatus, setSearchStatus] = useState('idle');
    const [attempt, setAttempt] = useState(0);
    const onSearchRef = useRef(onSearch);
    onSearchRef.current = onSearch;
    const source = onSearch ? (results ?? options) : options;
    // Remember every option seen, so a value keeps its label after the list changes.
    const known = useRef(new Map());
    for (const o of valueOptions ?? [])
        known.current.set(o.value, o);
    for (const o of source)
        known.current.set(o.value, o);
    const optionFor = (v) => source.find((o) => o.value === v) ?? known.current.get(v) ?? { value: v, label: v };
    const selected = !multiple && value != null ? optionFor(value) : null;
    const selectedLabel = selected?.label ?? '';
    // ----- Text -----
    const [internalInput, setInternalInput] = useState(textIsQuery ? '' : selectedLabel);
    const inputValue = inputProp !== undefined ? inputProp : internalInput;
    const setInput = (text) => {
        if (inputProp === undefined)
            setInternalInput(text);
        onInputValueChange?.(text);
    };
    // Keep the text in sync when the selection changes from outside.
    useEffect(() => {
        if (!textIsQuery && !open && inputProp === undefined)
            setInternalInput(selectedLabel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLabel]);
    const trimmed = inputValue.trim();
    const query = textIsQuery ? trimmed : trimmed !== '' && trimmed !== selectedLabel ? trimmed : '';
    const searchKey = onSearch && open ? query : null;
    useEffect(() => {
        if (searchKey === null)
            return;
        if (searchKey.length < minSearchLength) {
            setResults([]);
            setSearchStatus('idle');
            return;
        }
        const controller = new AbortController();
        setSearchStatus('loading');
        const timer = setTimeout(() => {
            const run = onSearchRef.current;
            if (!run)
                return;
            Promise.resolve()
                .then(() => run(searchKey, { signal: controller.signal }))
                .then((next) => {
                if (controller.signal.aborted)
                    return;
                setResults(next);
                setSearchStatus('idle');
            }, () => {
                if (controller.signal.aborted)
                    return;
                setSearchStatus('error');
            });
        }, searchKey === '' ? 0 : searchDebounce);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [searchKey, attempt, minSearchLength, searchDebounce]);
    const loading = loadingProp || searchStatus === 'loading';
    // ----- Visible list -----
    const { items, total } = useMemo(() => {
        let matches = onSearch || query === '' ? source : source.filter((o) => filter(o, query));
        if (sortByMatch && query !== '') {
            matches = matches
                .map((option, n) => ({ option, n, r: matchRank(option, query) }))
                .sort((a, b) => a.r - b.r || a.n - b.n)
                .map((x) => x.option);
        }
        const count = matches.length;
        if (limit !== undefined && matches.length > limit)
            matches = matches.slice(0, limit);
        // Group in order of first appearance; ungrouped options first.
        const groups = new Map([['', []]]);
        for (const o of matches) {
            const key = o.group ?? '';
            const list = groups.get(key);
            if (list)
                list.push(o);
            else
                groups.set(key, [o]);
        }
        const out = [];
        if (clearOption && query === '')
            out.push({ kind: 'clear' });
        for (const [group, list] of groups)
            for (const option of list)
                out.push({ kind: 'option', option, group });
        const lower = query.toLowerCase();
        if (onCreate &&
            query !== '' &&
            searchStatus !== 'loading' &&
            !source.some((o) => o.label.toLowerCase() === lower))
            out.push({ kind: 'create', query });
        return { items: out, total: count };
    }, [source, query, filter, sortByMatch, limit, onSearch, clearOption, onCreate, searchStatus]);
    // ----- Active option -----
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    // Opening (or mounting inline) puts the active option on the first selected one.
    useEffect(() => {
        if (!open)
            return;
        const at = items.findIndex((it) => (it.kind === 'option' && selectedSet.has(it.option.value)) ||
            (it.kind === 'clear' && selectedSet.size === 0));
        setActive(Math.max(0, at));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    useEffect(() => {
        setActive((a) => Math.min(a, Math.max(0, items.length - 1)));
    }, [items.length]);
    useEffect(() => {
        if (!open)
            return;
        const el = listRef.current?.querySelector(`[data-index="${active}"]`);
        el?.scrollIntoView?.({ block: 'nearest' });
    }, [active, open]);
    const activeId = open && items[active] ? `${listId}-opt-${active}` : undefined;
    // ----- Announcements (multiple) -----
    const [announcement, setAnnouncement] = useState('');
    // ----- Commit -----
    const commitSingle = (next) => {
        if (next)
            known.current.set(next.value, next);
        if (valueProp === undefined)
            setInternalValue(next?.value ?? null);
        onValueChange?.(next?.value ?? null);
        setInput(textIsQuery ? '' : (next?.label ?? ''));
        if (shouldClose)
            setOpen(false);
    };
    const commitValues = (next, changed, added) => {
        if (valuesProp === undefined)
            setInternalValues(next);
        onValuesChange?.(next);
        setAnnouncement(added ? messages.added(changed.label) : messages.removed(changed.label));
    };
    const toggle = (option) => {
        if (!multiple) {
            commitSingle(option);
            return;
        }
        known.current.set(option.value, option);
        const has = selectedSet.has(option.value);
        commitValues(has ? values.filter((v) => v !== option.value) : [...values, option.value], option, !has);
        if (trimmed !== '')
            setInput('');
        if (shouldClose)
            setOpen(false);
    };
    const removeValue = (v) => {
        commitValues(values.filter((x) => x !== v), optionFor(v), false);
    };
    const choose = (item) => {
        if (!item || itemDisabled(item))
            return;
        if (item.kind === 'option')
            toggle(item.option);
        else if (item.kind === 'clear') {
            if (multiple) {
                if (valuesProp === undefined)
                    setInternalValues([]);
                onValuesChange?.([]);
                if (shouldClose)
                    setOpen(false);
            }
            else
                commitSingle(null);
        }
        else if (onCreate) {
            void Promise.resolve(onCreate(item.query)).then((created) => {
                if (created) {
                    if (multiple && selectedSet.has(created.value))
                        setInput('');
                    else
                        toggle(created);
                }
                else {
                    setInput(textIsQuery ? '' : selectedLabel);
                    if (!multiple)
                        setOpen(false);
                }
            });
        }
    };
    const retry = () => setAttempt((n) => n + 1);
    const handleKey = (e) => {
        // Radix's dismissable layer prevents default on Escape before this runs,
        // so only a consumer's own preventDefault stops the built-in handling.
        const preventedBefore = e.defaultPrevented;
        onKeyDown?.(e);
        if (e.defaultPrevented && !preventedBefore)
            return;
        const last = items.length - 1;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!open)
                    setOpen(true);
                else if (!e.altKey)
                    setActive((a) => Math.min(a + 1, last));
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (e.altKey) {
                    if (open)
                        setOpen(false);
                }
                else if (!open)
                    setOpen(true);
                else
                    setActive((a) => Math.max(a - 1, 0));
                break;
            case 'PageDown':
                if (open) {
                    e.preventDefault();
                    setActive((a) => Math.min(a + 10, last));
                }
                break;
            case 'PageUp':
                if (open) {
                    e.preventDefault();
                    setActive((a) => Math.max(a - 10, 0));
                }
                break;
            case 'Home':
                if (open && items.length > 0) {
                    e.preventDefault();
                    setActive(0);
                }
                break;
            case 'End':
                if (open && items.length > 0) {
                    e.preventDefault();
                    setActive(last);
                }
                break;
            case 'Enter':
                if (open) {
                    e.preventDefault();
                    if (searchStatus === 'error' && items.length === 0)
                        retry();
                    else
                        choose(items[active]);
                }
                break;
            case 'Escape':
                if (open && !inline) {
                    e.preventDefault();
                    setOpen(false);
                    setInput(textIsQuery ? '' : selectedLabel);
                }
                break;
            case 'Backspace':
                if (inputValue !== '')
                    break;
                if (multiple && values.length > 0) {
                    const lastValue = values[values.length - 1];
                    if (lastValue !== undefined)
                        removeValue(lastValue);
                }
                else if (!multiple && !inline && clearable && selected)
                    commitSingle(null);
                break;
        }
    };
    const fieldProps = useFieldControl(rest, { invalid });
    // The listbox is named by `listLabel`, else the Field label, else the input's aria-label.
    const listLabelledBy = !listLabel && field ? field.labelId : undefined;
    const listName = listLabel ?? rest['aria-label'];
    const isInvalid = fieldProps['aria-invalid'] === true;
    const renderDefault = (o, isSelected) => (_jsxs(_Fragment, { children: [o.media ? (_jsx("span", { className: "mn-combobox__media", "aria-hidden": true, children: o.media })) : null, _jsxs("span", { className: "mn-menu__text", children: [o.label, o.description ? _jsx("span", { className: "mn-combobox__description", children: o.description }) : null] }), o.hint ? _jsx("span", { className: "mn-combobox__hint", children: o.hint }) : null, isSelected ? (_jsx("span", { className: "mn-menu__indicator", children: _jsx(CheckGlyph, {}) })) : null] }));
    const renderItem = (item, i) => {
        const isActive = i === active;
        let isSelected = false;
        let content;
        let key;
        let disabled = false;
        if (item.kind === 'option') {
            isSelected = selectedSet.has(item.option.value);
            disabled = !!item.option.disabled;
            key = `o:${item.group}:${item.option.value}`;
            content = renderOption
                ? renderOption(item.option, { selected: isSelected, active: isActive })
                : renderDefault(item.option, isSelected);
        }
        else if (item.kind === 'clear' && clearOption) {
            isSelected = selectedSet.size === 0;
            key = 'clear';
            content = renderDefault({ value: '', ...clearOption }, isSelected);
        }
        else {
            key = 'create';
            content = (_jsxs(_Fragment, { children: [_jsx("span", { className: "mn-combobox__media mn-combobox__create-icon", "aria-hidden": true, children: "+" }), _jsx("span", { className: "mn-menu__text", children: messages.create(item.kind === 'create' ? item.query : '') })] }));
        }
        return (
        // Options follow the aria-activedescendant pattern: the input keeps
        // focus and handles every key, so options are deliberately not focusable.
        // monarch-ignore-next-line a11y/click-without-key -- aria-activedescendant pattern: the input handles all keys
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
        _jsx("div", { id: `${listId}-opt-${i}`, role: "option", "aria-selected": isSelected, "aria-disabled": disabled || undefined, "data-index": i, "data-highlighted": isActive || undefined, "data-kind": item.kind === 'option' ? undefined : item.kind, className: "mn-menu__item", onMouseEnter: () => setActive(i), onMouseDown: (e) => e.preventDefault(), onClick: () => choose(item), children: content }, key));
    };
    // Split rendered items into groups for role="group" wrappers.
    const blocks = [];
    items.forEach((item, i) => {
        const group = item.kind === 'option' ? item.group : '';
        const prev = blocks[blocks.length - 1];
        if (prev && prev.group === group)
            prev.end = i;
        else
            blocks.push({ group, start: i, end: i });
    });
    const optionCount = items.filter((it) => it.kind === 'option').length;
    let status = null;
    if (searchStatus === 'error') {
        status = (_jsxs(_Fragment, { children: [messages.error, ' ', _jsxs("button", { type: "button", className: "mn-combobox__retry", onMouseDown: (e) => e.preventDefault(), onClick: () => {
                        retry();
                        inputRef.current?.focus();
                    }, children: [_jsx(RetryGlyph, {}), messages.retry] })] }));
    }
    else if (loading && optionCount === 0)
        status = messages.loading;
    else if (optionCount === 0 && !items.some((it) => it.kind === 'create'))
        status = emptyMessage;
    else if (limit !== undefined && total > limit)
        status = messages.more(limit, total);
    const list = (_jsxs(_Fragment, { children: [_jsx("div", { ref: listRef, id: listId, role: "listbox", "aria-label": listLabelledBy ? undefined : (listName ?? 'Options'), "aria-labelledby": listLabelledBy, "aria-multiselectable": multiple || undefined, "aria-busy": loading || undefined, className: "mn-combobox__list", "data-inline": inline || undefined, children: blocks.map((b) => {
                    const children = items
                        .slice(b.start, b.end + 1)
                        .map((it, k) => renderItem(it, b.start + k));
                    if (!b.group)
                        return children;
                    const headingId = `${listId}-group-${b.start}`;
                    return (_jsxs("div", { role: "group", "aria-labelledby": headingId, children: [_jsx("div", { id: headingId, role: "presentation", className: "mn-menu__label", children: b.group }), children] }, `g:${b.group}`));
                }) }), status !== null ? (_jsx("div", { className: "mn-combobox__status", role: "status", "data-tone": searchStatus === 'error' ? 'danger' : undefined, children: status })) : null] }));
    const input = (_jsx("input", { ref: (node) => {
            inputRef.current = node;
            if (typeof ref === 'function')
                ref(node);
            else if (ref)
                ref.current = node;
        }, ...fieldProps, type: "text", role: "combobox", autoComplete: "off", "aria-autocomplete": "list", "aria-expanded": open, "aria-controls": open ? listId : undefined, "aria-activedescendant": activeId, className: "mn-combobox__input", placeholder: multiple && values.length > 0 && !inline ? undefined : placeholder, value: inputValue, onChange: (e) => {
            setInput(e.target.value);
            setActive(0);
            if (!open)
                setOpen(true);
        }, onKeyDown: handleKey, onBlur: (e) => {
            onBlur?.(e);
            if (inline)
                return;
            // Revert unmatched text so the input always reflects the selection.
            if (!listRef.current?.parentElement?.contains(e.relatedTarget))
                setInput(textIsQuery ? '' : selectedLabel);
        } }));
    const live = multiple ? (_jsx("span", { className: "mn-visually-hidden", role: "status", children: announcement })) : null;
    if (inline) {
        return (_jsxs("div", { className: cx('mn-combobox__panel', className), "data-size": size, "data-disabled": fieldProps.disabled || undefined, children: [_jsxs("div", { className: "mn-combobox__search", "data-invalid": isInvalid || undefined, children: [_jsx(SearchGlyph, { className: "mn-combobox__search-icon" }), input, loading ? _jsx(Spinner, { size: "sm", className: "mn-combobox__spinner" }) : null] }), list, live] }));
    }
    const chips = multiple && values.length > 0 ? (_jsx("ul", { className: "mn-combobox__chips", children: values.map((v) => {
            const o = optionFor(v);
            return (_jsxs("li", { className: "mn-combobox__chip", children: [o.media ? (_jsx("span", { className: "mn-combobox__chip-media", "aria-hidden": true, children: o.media })) : null, _jsx("span", { className: "mn-combobox__chip-label", children: o.label }), fieldProps.disabled || rest.readOnly ? null : (_jsx("button", { type: "button", className: "mn-combobox__chip-remove", "aria-label": messages.remove(o.label), onMouseDown: (e) => e.preventDefault(), onClick: () => {
                            removeValue(v);
                            inputRef.current?.focus();
                        }, children: _jsx(CloseGlyph, {}) }))] }, v));
        }) })) : null;
    return (_jsxs(PopoverPrimitive.Root, { open: open, onOpenChange: (next) => {
            setOpen(next);
            if (!next)
                setInput(textIsQuery ? '' : selectedLabel);
        }, children: [_jsx(PopoverPrimitive.Anchor, { asChild: true, children: _jsxs("div", { className: cx('mn-input', 'mn-combobox', className), "data-size": size, "data-disabled": fieldProps.disabled || undefined, "data-invalid": isInvalid || undefined, "data-multiple": multiple || undefined, children: [chips, input, loading ? _jsx(Spinner, { size: "sm", className: "mn-combobox__spinner" }) : null, _jsx("button", { type: "button", tabIndex: -1, "aria-label": open ? messages.closeOptions : messages.showOptions, className: "mn-combobox__toggle", disabled: fieldProps.disabled, onMouseDown: (e) => e.preventDefault(), onClick: () => {
                                setOpen(!open);
                                inputRef.current?.focus();
                            }, children: _jsx(ChevronDownGlyph, {}) }), live] }) }), _jsx(PopoverPrimitive.Portal, { children: _jsx(PopoverPrimitive.Content, { align: "start", sideOffset: 4, onOpenAutoFocus: (e) => e.preventDefault(), onCloseAutoFocus: (e) => e.preventDefault(), onInteractOutside: (e) => {
                        if (inputRef.current?.parentElement?.contains(e.target))
                            e.preventDefault();
                    }, 
                    // The popup is the listbox inside, not a dialog: the input keeps focus throughout.
                    role: "presentation", className: "mn-menu mn-combobox__content", children: list }) })] }));
});
//# sourceMappingURL=combobox.js.map